import { useMemo } from "react";
import type { ProviderId } from "@/lib/powtoon-be/types";
import type { PickerOption } from "@/lib/liveavatar/options";
import { useAvatarOptions, useLanguageOptions, useVoiceOptions } from "@/lib/liveavatar/options";
import { liveavatar } from "@/lib/liveavatar/client";
import { useTavusFaces, useElevenLabsVoices, useFaceGender } from "./tavus/options";
import { useTavusLanguages } from "./tavus/languages";
import { useDidPresenters, useDidVoices, useDidLocaleList } from "./did/options";
import { useKalturaVisuals, useKalturaVoices } from "./kaltura/options";
import { liveAvatarVoiceOptions } from "./liveavatar/voiceSelection";

/**
 * Sentinel id for "use the provider's default voice" (currently used by
 * Tavus so the user can opt to keep the face's built-in replica voice).
 * The form maps this back to `""` before persisting.
 */
export const DEFAULT_VOICE_ID = "__default__";

export type PickerList = {
  options: PickerOption[];
  isLoading: boolean;
  error: Error | null;
};

export type VoicePickerList = PickerList & {
  previewFetcher?: (o: PickerOption) => Promise<{ audio_base64: string; mime: string }>;
};

export type ProviderPickers = {
  avatarLabel: string;
  voiceLabel: string;
  languageRequired: boolean;
  showVoicePreviewControl: boolean;
  /** Whether the provider exposes separate voice/language pickers. */
  showVoicePicker: boolean;
  avatars: PickerList;
  voices: VoicePickerList;
  languages: PickerList;
  /**
   * Suggest voice/language defaults when the user picks a new avatar.
   * Returning `undefined` for a field means "leave it alone"; returning
   * `""` means "clear it".
   */
  deriveDefaults(
    avatar: PickerOption | undefined,
    ctx: { voices: PickerOption[]; languages: PickerOption[] },
  ): { voiceId?: string; language?: string };
};

/**
 * Unified picker data for the scenario form. Both provider data sets are
 * fetched behind `enabled` gates so switching providers doesn't refetch
 * the inactive one, and the hook order stays stable.
 */
export function useProviderPickers(
  provider: ProviderId,
  args: {
    avatarId?: string;
    importedVoicesOnly?: boolean;
    language?: string;
    includeUserAvatars?: boolean;
    importedVoicesFirst?: boolean;
    selectedVoiceId?: string;
  } = {},
): ProviderPickers {
  const isTavus = provider === "tavus";
  const isDid = provider === "did";
  const isKaltura = provider === "kaltura";

  // LiveAvatar
  const laAvatars = useAvatarOptions({ includeUserAvatars: args.includeUserAvatars });
  const laVoicesQ = useVoiceOptions();
  const laLanguages = useLanguageOptions();
  const laVoices = useMemo<PickerOption[]>(
    () => liveAvatarVoiceOptions(laVoicesQ.options, {
      importedVoicesOnly: args.importedVoicesOnly,
      importedFirst: args.importedVoicesFirst,
      selectedVoiceId: args.selectedVoiceId,
    }),
    [laVoicesQ.options, args.importedVoicesOnly, args.importedVoicesFirst, args.selectedVoiceId],
  );

  // Tavus
  const tvFacesQ = useTavusFaces({ enabled: isTavus });
  const tvVoicesQ = useElevenLabsVoices({ enabled: isTavus });
  const tvLanguagesQ = useTavusLanguages({ enabled: isTavus });
  const faceGender = useFaceGender(isTavus ? args.avatarId : undefined);

  const tvAvatars = useMemo<PickerOption[]>(
    () =>
      tvFacesQ.options.map((f) => ({
        id: f.id,
        label: f.label,
        preview_url: f.thumbnail_url,
        sublabel: f.face_type,
      })),
    [tvFacesQ.options],
  );

  const tvVoices = useMemo<PickerOption[]>(() => {
    const filtered = faceGender
      ? tvVoicesQ.options.filter((o) => o.gender?.toLowerCase() === faceGender)
      : tvVoicesQ.options;
    const mapped: PickerOption[] = filtered.map((o) => ({
      id: o.id,
      label: o.label,
      sublabel: o.accent || undefined,
      preview_url: o.preview_url,
      gender: o.gender,
    }));
    return [{ id: DEFAULT_VOICE_ID, label: "Face default" }, ...mapped];
  }, [tvVoicesQ.options, faceGender]);

  // D-ID
  const didPresentersQ = useDidPresenters({ enabled: isDid });
  const didVoicesQ = useDidVoices({
    enabled: isDid,
    locale: args.language,
  });
  const didLanguagesQ = useDidLocaleList({ enabled: isDid });

  // Kaltura — faces and voices come straight from the catalog; each scenario
  // owns its own pair (provisioned at save time).
  const kalVisualsQ = useKalturaVisuals({ enabled: isKaltura, keepId: args.avatarId });
  const kalVoicesQ = useKalturaVoices({ enabled: isKaltura });

  if (isKaltura) {
    const lang = args.language?.trim().toLowerCase();
    const kalVoices = lang
      ? kalVoicesQ.options.filter((o) => (o.sublabel ?? "").toLowerCase().startsWith(lang))
      : kalVoicesQ.options;
    return {
      avatarLabel: "Face",
      voiceLabel: "Voice",
      languageRequired: false,
      showVoicePreviewControl: false,
      showVoicePicker: true,
      avatars: {
        options: kalVisualsQ.options,
        isLoading: kalVisualsQ.isLoading,
        error: kalVisualsQ.error,
      },
      voices: {
        options: kalVoices,
        isLoading: kalVoicesQ.isLoading,
        error: kalVoicesQ.error,
      },
      languages: {
        options: kalVoicesQ.languages.map((l) => ({ id: l, label: l })),
        isLoading: kalVoicesQ.isLoading,
        error: kalVoicesQ.error,
      },
      deriveDefaults(_avatar, ctx) {
        // Picking a face says nothing about the voice in Kaltura — just seed
        // the first voice available under the current language filter.
        return { voiceId: ctx.voices[0]?.id ?? "" };
      },
    };
  }

  if (isTavus) {
    return {
      avatarLabel: "Avatar",
      voiceLabel: "Voice",
      languageRequired: false,
      showVoicePreviewControl: false,
      showVoicePicker: true,
      avatars: {
        options: tvAvatars,
        isLoading: tvFacesQ.isLoading,
        error: tvFacesQ.error,
      },
      voices: {
        options: tvVoices,
        isLoading: tvVoicesQ.isLoading,
        error: tvVoicesQ.error,
      },
      languages: {
        options: tvLanguagesQ.options,
        isLoading: tvLanguagesQ.isLoading,
        error: tvLanguagesQ.error,
      },
      deriveDefaults(_avatar, ctx) {
        // Picking a new face drops the previously-selected voice — it was
        // filtered/tied to that face's gender. Also seed English as the
        // language default so create_conversation always carries one.
        const en = ctx.languages.find((o) => /^en(glish)?$/i.test(o.id)) ?? ctx.languages[0];
        return { voiceId: DEFAULT_VOICE_ID, language: en?.id };
      },
    };
  }

  if (isDid) {
    const didPickedAvatar = args.avatarId
      ? didPresentersQ.options.find((o) => o.id === args.avatarId)
      : undefined;
    const didGender = didPickedAvatar?.gender;
    // Gender matching is our own nicety — D-ID puts no restriction on which
    // voice an avatar uses. So prefer matching voices, but never leave the
    // list empty (a narrow language may have none of that gender).
    const didGenderMatched = didGender
      ? didVoicesQ.options.filter((o) => o.gender === didGender)
      : didVoicesQ.options;
    const didFilteredVoices = didGenderMatched.length
      ? didGenderMatched
      : didVoicesQ.options;

    return {
      avatarLabel: "Avatar",
      voiceLabel: "Voice",
      languageRequired: false,
      showVoicePreviewControl: false,
      showVoicePicker: true,
      avatars: {
        options: didPresentersQ.options,
        isLoading: didPresentersQ.isLoading,
        error: didPresentersQ.error,
      },
      voices: {
        options: didFilteredVoices,
        isLoading: didVoicesQ.isLoading,
        error: didVoicesQ.error,
      },
      languages: {
        options: didLanguagesQ.options,
        isLoading: didLanguagesQ.isLoading,
        error: didLanguagesQ.error,
      },
      deriveDefaults(_avatar, ctx) {
        // Voice list is already gender/locale-filtered above — just pick
        // the first available voice and seed English as the language.
        const voiceId = ctx.voices[0]?.id ?? "";
        const en = ctx.languages.find((o) => /^en(-|$)/i.test(o.id)) ?? ctx.languages[0];
        return { voiceId, language: en?.id };
      },
    };
  }

  return {
    avatarLabel: "Avatar",
    voiceLabel: "Voice",
    languageRequired: true,
    showVoicePreviewControl: true,
    showVoicePicker: true,
    avatars: {
      options: laAvatars.options,
      isLoading: laAvatars.isLoading,
      error: laAvatars.error,
    },
    voices: {
      options: laVoices,
      isLoading: laVoicesQ.isLoading,
      error: laVoicesQ.error,
      previewFetcher: (opt) => {
        const text = "Hi there! This is a preview of how I'll sound.";
        if (opt.external_voice_id) return liveavatar.ttsPreview(opt.external_voice_id, text);
        return liveavatar.getVoicePreview(opt.id);
      },
    },
    languages: {
      options: laLanguages.options,
      isLoading: laLanguages.isLoading,
      error: laLanguages.error,
    },
    deriveDefaults(avatar, ctx) {
      if (!avatar) return {};
      const defId = avatar.default_voice_id;
      const def = defId ? ctx.voices.find((o) => o.id === defId) : undefined;
      let voiceId: string | undefined;
      if (def) voiceId = def.id;
      else if (avatar.gender) {
        const match = ctx.voices.find((o) => o.gender === avatar.gender);
        voiceId = match ? match.id : "";
      } else {
        voiceId = "";
      }
      const enLang = ctx.languages.find((o) => /^en(-|$)/i.test(o.id));
      return { voiceId, language: enLang?.id };
    },
  };
}
